/**
 * Crafting system - handles recipes for 2x2 (hand) and 3x3 (crafting table) grids
 *
 * Supports shaped recipes (pattern matters) and shapeless recipes.
 * Furnace recipes are separate (smelting).
 */

import { BlockId } from '../world/BlockType.js';
import { createTool } from './ToolSystem.js';
import { ItemId } from './ItemRegistry.js';

/**
 * A recipe definition:
 * {
 *   pattern: string[] (rows of characters, e.g. ['MMM', ' S ', ' S '] for pickaxe)
 *   key: { char: { blockId } or { itemId } } -- maps pattern chars to ingredients
 *   result: { blockId, count } or { itemId, count } or { toolType, material } (for tools)
 *   shapeless: boolean (default false)
 *   gridSize: 2 or 3 (minimum grid size needed)
 * }
 */

// Helper: create a block ingredient
function B(blockId) { return { blockId }; }
// Helper: create an item ingredient
function I(itemId) { return { itemId }; }

// All shaped recipes
const SHAPED_RECIPES = [
  // ===== BASIC RECIPES (2x2 compatible) =====

  // 1 WOOD log -> 4 PLANKS (shapeless, 1x1)
  {
    pattern: ['W'],
    key: { 'W': B(BlockId.WOOD) },
    result: { blockId: BlockId.PLANKS, count: 4 },
    shapeless: true,
    gridSize: 2,
  },

  // 2 PLANKS vertical -> 4 sticks
  {
    pattern: ['P', 'P'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { itemId: ItemId.STICK, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // PLANKS in 2x2 -> CRAFTING_TABLE
  {
    pattern: ['PP', 'PP'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.CRAFTING_TABLE, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // COBBLESTONE in 2x2 -> STONE
  {
    pattern: ['CC', 'CC'],
    key: { 'C': B(BlockId.COBBLESTONE) },
    result: { blockId: BlockId.STONE, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // ===== 3x3 RECIPES =====

  // 8 COBBLESTONE around edge -> FURNACE
  {
    pattern: ['CCC', 'C C', 'CCC'],
    key: { 'C': B(BlockId.COBBLESTONE) },
    result: { blockId: BlockId.FURNACE, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // 3 PLANKS top + 3 PLANKS bottom + 3 WOOD middle -> BOOKSHELF (simplified)
  // We don't have a bookshelf block yet, so skip this one

  // ===== PICKAXE RECIPES (3x3) =====
  // Pattern: MMM / _S_ / _S_

  // Wood pickaxe
  {
    pattern: ['MMM', ' S ', ' S '],
    key: { 'M': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { toolType: 'pickaxe', material: 'wood' },
    shapeless: false,
    gridSize: 3,
  },
  // Stone pickaxe
  {
    pattern: ['MMM', ' S ', ' S '],
    key: { 'M': B(BlockId.COBBLESTONE), 'S': I(ItemId.STICK) },
    result: { toolType: 'pickaxe', material: 'stone' },
    shapeless: false,
    gridSize: 3,
  },
  // Iron pickaxe
  {
    pattern: ['MMM', ' S ', ' S '],
    key: { 'M': I(ItemId.IRON_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'pickaxe', material: 'iron' },
    shapeless: false,
    gridSize: 3,
  },
  // Gold pickaxe
  {
    pattern: ['MMM', ' S ', ' S '],
    key: { 'M': I(ItemId.GOLD_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'pickaxe', material: 'gold' },
    shapeless: false,
    gridSize: 3,
  },
  // Diamond pickaxe
  {
    pattern: ['MMM', ' S ', ' S '],
    key: { 'M': I(ItemId.DIAMOND_GEM), 'S': I(ItemId.STICK) },
    result: { toolType: 'pickaxe', material: 'diamond' },
    shapeless: false,
    gridSize: 3,
  },

  // ===== AXE RECIPES (3x3) =====
  // Pattern: MM_ / MS_ / _S_

  // Wood axe
  {
    pattern: ['MM', 'MS', ' S'],
    key: { 'M': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { toolType: 'axe', material: 'wood' },
    shapeless: false,
    gridSize: 3,
  },
  // Stone axe
  {
    pattern: ['MM', 'MS', ' S'],
    key: { 'M': B(BlockId.COBBLESTONE), 'S': I(ItemId.STICK) },
    result: { toolType: 'axe', material: 'stone' },
    shapeless: false,
    gridSize: 3,
  },
  // Iron axe
  {
    pattern: ['MM', 'MS', ' S'],
    key: { 'M': I(ItemId.IRON_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'axe', material: 'iron' },
    shapeless: false,
    gridSize: 3,
  },
  // Gold axe
  {
    pattern: ['MM', 'MS', ' S'],
    key: { 'M': I(ItemId.GOLD_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'axe', material: 'gold' },
    shapeless: false,
    gridSize: 3,
  },
  // Diamond axe
  {
    pattern: ['MM', 'MS', ' S'],
    key: { 'M': I(ItemId.DIAMOND_GEM), 'S': I(ItemId.STICK) },
    result: { toolType: 'axe', material: 'diamond' },
    shapeless: false,
    gridSize: 3,
  },

  // ===== SHOVEL RECIPES (3x3) =====
  // Pattern: _M_ / _S_ / _S_

  // Wood shovel
  {
    pattern: ['M', 'S', 'S'],
    key: { 'M': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { toolType: 'shovel', material: 'wood' },
    shapeless: false,
    gridSize: 3,
  },
  // Stone shovel
  {
    pattern: ['M', 'S', 'S'],
    key: { 'M': B(BlockId.COBBLESTONE), 'S': I(ItemId.STICK) },
    result: { toolType: 'shovel', material: 'stone' },
    shapeless: false,
    gridSize: 3,
  },
  // Iron shovel
  {
    pattern: ['M', 'S', 'S'],
    key: { 'M': I(ItemId.IRON_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'shovel', material: 'iron' },
    shapeless: false,
    gridSize: 3,
  },
  // Gold shovel
  {
    pattern: ['M', 'S', 'S'],
    key: { 'M': I(ItemId.GOLD_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'shovel', material: 'gold' },
    shapeless: false,
    gridSize: 3,
  },
  // Diamond shovel
  {
    pattern: ['M', 'S', 'S'],
    key: { 'M': I(ItemId.DIAMOND_GEM), 'S': I(ItemId.STICK) },
    result: { toolType: 'shovel', material: 'diamond' },
    shapeless: false,
    gridSize: 3,
  },

  // ===== SWORD RECIPES (3x3) =====
  // Pattern: _M_ / _M_ / _S_

  // Wood sword
  {
    pattern: ['M', 'M', 'S'],
    key: { 'M': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { toolType: 'sword', material: 'wood' },
    shapeless: false,
    gridSize: 3,
  },
  // Stone sword
  {
    pattern: ['M', 'M', 'S'],
    key: { 'M': B(BlockId.COBBLESTONE), 'S': I(ItemId.STICK) },
    result: { toolType: 'sword', material: 'stone' },
    shapeless: false,
    gridSize: 3,
  },
  // Iron sword
  {
    pattern: ['M', 'M', 'S'],
    key: { 'M': I(ItemId.IRON_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'sword', material: 'iron' },
    shapeless: false,
    gridSize: 3,
  },
  // Gold sword
  {
    pattern: ['M', 'M', 'S'],
    key: { 'M': I(ItemId.GOLD_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'sword', material: 'gold' },
    shapeless: false,
    gridSize: 3,
  },
  // Diamond sword
  {
    pattern: ['M', 'M', 'S'],
    key: { 'M': I(ItemId.DIAMOND_GEM), 'S': I(ItemId.STICK) },
    result: { toolType: 'sword', material: 'diamond' },
    shapeless: false,
    gridSize: 3,
  },

  // ===== HOE RECIPES (3x3) =====
  // Pattern: MM_ / _S_ / _S_

  // Wood hoe
  {
    pattern: ['MM', ' S', ' S'],
    key: { 'M': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { toolType: 'hoe', material: 'wood' },
    shapeless: false,
    gridSize: 3,
  },
  // Stone hoe
  {
    pattern: ['MM', ' S', ' S'],
    key: { 'M': B(BlockId.COBBLESTONE), 'S': I(ItemId.STICK) },
    result: { toolType: 'hoe', material: 'stone' },
    shapeless: false,
    gridSize: 3,
  },
  // Iron hoe
  {
    pattern: ['MM', ' S', ' S'],
    key: { 'M': I(ItemId.IRON_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'hoe', material: 'iron' },
    shapeless: false,
    gridSize: 3,
  },
  // Gold hoe
  {
    pattern: ['MM', ' S', ' S'],
    key: { 'M': I(ItemId.GOLD_INGOT), 'S': I(ItemId.STICK) },
    result: { toolType: 'hoe', material: 'gold' },
    shapeless: false,
    gridSize: 3,
  },
  // Diamond hoe
  {
    pattern: ['MM', ' S', ' S'],
    key: { 'M': I(ItemId.DIAMOND_GEM), 'S': I(ItemId.STICK) },
    result: { toolType: 'hoe', material: 'diamond' },
    shapeless: false,
    gridSize: 3,
  },

  // ===== UTILITY RECIPES =====

  // Torch: coal + stick -> 4 torches
  {
    pattern: ['C', 'S'],
    key: { 'C': I(ItemId.COAL), 'S': I(ItemId.STICK) },
    result: { blockId: BlockId.TORCH, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // Chest: 8 planks around edge
  {
    pattern: ['PPP', 'P P', 'PPP'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.CHEST, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Bed: 3 wool on top + 3 planks on bottom
  {
    pattern: ['WWW', 'PPP'],
    key: { 'W': B(BlockId.WOOL_WHITE), 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.BED, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Bread: 3 wheat
  {
    pattern: ['WWW'],
    key: { 'W': I(ItemId.WHEAT_ITEM) },
    result: { itemId: ItemId.BREAD, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Ladder: sticks in H pattern -> 3 ladders
  {
    pattern: ['S S', 'SSS', 'S S'],
    key: { 'S': I(ItemId.STICK) },
    result: { blockId: BlockId.LADDER, count: 3 },
    shapeless: false,
    gridSize: 3,
  },

  // Fence: planks + sticks -> 3 fences
  {
    pattern: ['PSP', 'PSP'],
    key: { 'P': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { blockId: BlockId.FENCE, count: 3 },
    shapeless: false,
    gridSize: 3,
  },

  // Stone slab: 3 stone across bottom -> 6 slabs
  {
    pattern: ['SSS'],
    key: { 'S': B(BlockId.STONE) },
    result: { blockId: BlockId.SLAB_STONE, count: 6 },
    shapeless: false,
    gridSize: 3,
  },

  // Wood slab: 3 planks across bottom -> 6 slabs
  {
    pattern: ['PPP'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.SLAB_WOOD, count: 6 },
    shapeless: false,
    gridSize: 3,
  },

  // Bricks: 4 clay in 2x2 (simplified)
  {
    pattern: ['CC', 'CC'],
    key: { 'C': B(BlockId.CLAY) },
    result: { blockId: BlockId.BRICKS, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // Bookshelf: 3 planks + 3 books (no book item, so planks+wool placeholder) -> simplified: 6 planks
  // Actually: planks top + leather middle + planks bottom
  {
    pattern: ['PPP', 'LLL', 'PPP'],
    key: { 'P': B(BlockId.PLANKS), 'L': I(ItemId.LEATHER) },
    result: { blockId: BlockId.BOOKSHELF, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Iron block: 9 iron ingots
  {
    pattern: ['III', 'III', 'III'],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { blockId: BlockId.IRON_BLOCK, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Gold block: 9 gold ingots
  {
    pattern: ['GGG', 'GGG', 'GGG'],
    key: { 'G': I(ItemId.GOLD_INGOT) },
    result: { blockId: BlockId.GOLD_BLOCK, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Diamond block: 9 diamonds
  {
    pattern: ['DDD', 'DDD', 'DDD'],
    key: { 'D': I(ItemId.DIAMOND_GEM) },
    result: { blockId: BlockId.DIAMOND_BLOCK, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Iron block -> 9 iron ingots (shapeless decompose)
  {
    pattern: ['I'],
    key: { 'I': B(BlockId.IRON_BLOCK) },
    result: { itemId: ItemId.IRON_INGOT, count: 9 },
    shapeless: true,
    gridSize: 2,
  },

  // Gold block -> 9 gold ingots
  {
    pattern: ['G'],
    key: { 'G': B(BlockId.GOLD_BLOCK) },
    result: { itemId: ItemId.GOLD_INGOT, count: 9 },
    shapeless: true,
    gridSize: 2,
  },

  // Diamond block -> 9 diamonds
  {
    pattern: ['D'],
    key: { 'D': B(BlockId.DIAMOND_BLOCK) },
    result: { itemId: ItemId.DIAMOND_GEM, count: 9 },
    shapeless: true,
    gridSize: 2,
  },

  // TNT: 5 gunpowder + 4 sand
  {
    pattern: ['GSG', 'SGS', 'GSG'],
    key: { 'G': I(ItemId.GUNPOWDER), 'S': B(BlockId.SAND) },
    result: { blockId: BlockId.TNT, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Glass pane: 6 glass in 2 rows -> just give glass blocks (simplified)
  // Glowstone: 4 glowstone dust (we don't have dust, so skip for now)

  // Nether bricks: 4 netherrack in 2x2
  {
    pattern: ['NN', 'NN'],
    key: { 'N': B(BlockId.NETHERRACK) },
    result: { blockId: BlockId.NETHER_BRICKS, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // Snow block: 4 snow in 2x2 (simplified from snowballs)
  {
    pattern: ['SS', 'SS'],
    key: { 'S': B(BlockId.SNOW) },
    result: { blockId: BlockId.ICE, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Moss stone: cobblestone + leaves (vine substitute)
  {
    pattern: ['CL'],
    key: { 'C': B(BlockId.COBBLESTONE), 'L': B(BlockId.LEAVES) },
    result: { blockId: BlockId.MOSS_STONE, count: 1 },
    shapeless: true,
    gridSize: 2,
  },

  // Bone -> 3 bone meal (shapeless)
  {
    pattern: ['B'],
    key: { 'B': I(ItemId.BONE) },
    result: { itemId: ItemId.BONE_MEAL, count: 3 },
    shapeless: true,
    gridSize: 2,
  },

  // Door: 6 planks
  {
    pattern: ['PP', 'PP', 'PP'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.DOOR_CLOSED, count: 3 },
    shapeless: false,
    gridSize: 3,
  },

  // Glass (from sand blocks in crafting — simplified, since glass panes don't exist)
  // Pumpkin pie: pumpkin + sugar (egg) → simplified: pumpkin + wheat
  // Melon seeds: melon -> seeds (no melon seeds yet, skip)

  // Wool: 4 string -> 1 wool
  {
    pattern: ['SS', 'SS'],
    key: { 'S': I(ItemId.STRING) },
    result: { blockId: BlockId.WOOL_WHITE, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Birch planks: birch log -> 4 planks
  {
    pattern: ['W'],
    key: { 'W': B(BlockId.LOG_BIRCH) },
    result: { blockId: BlockId.PLANKS, count: 4 },
    shapeless: true,
    gridSize: 2,
  },

  // Jungle planks: jungle log -> 4 planks
  {
    pattern: ['W'],
    key: { 'W': B(BlockId.JUNGLE_LOG) },
    result: { blockId: BlockId.PLANKS, count: 4 },
    shapeless: true,
    gridSize: 2,
  },

  // Acacia planks: acacia log -> 4 planks
  {
    pattern: ['W'],
    key: { 'W': B(BlockId.ACACIA_LOG) },
    result: { blockId: BlockId.PLANKS, count: 4 },
    shapeless: true,
    gridSize: 2,
  },

  // ===== BOW & ARROW RECIPES =====

  // Bow: 3 sticks + 3 string
  // Pattern:  _SH / S_H / _SH  (S=stick, H=string)
  {
    pattern: [' SH', 'S H', ' SH'],
    key: { 'S': I(ItemId.STICK), 'H': I(ItemId.STRING) },
    result: { itemId: ItemId.BOW, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Arrow: flint + stick + feather -> 4 arrows
  {
    pattern: ['F', 'S', 'P'],
    key: { 'F': I(ItemId.FLINT), 'S': I(ItemId.STICK), 'P': I(ItemId.FEATHER) },
    result: { itemId: ItemId.ARROW_ITEM, count: 4 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== ENCHANTING TABLE =====
  // Pattern: _P_ / DOD / _O_  (P=planks, D=diamond, O=obsidian)
  {
    pattern: [' P ', 'DOD', ' O '],
    key: { 'P': B(BlockId.PLANKS), 'D': I(ItemId.DIAMOND_GEM), 'O': B(BlockId.OBSIDIAN) },
    result: { blockId: BlockId.ENCHANTING_TABLE, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== ADDITIONAL BLOCK RECIPES =====

  // Glass pane from glass (simplified: 6 glass -> 16 glass panes = just more glass)
  // Stairs: 6 blocks in stair pattern -> 4 stairs (using slabs as proxy)

  // Sandstone: 4 sand in 2x2
  {
    pattern: ['SS', 'SS'],
    key: { 'S': B(BlockId.SAND) },
    result: { blockId: BlockId.SANDSTONE, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // Flint from gravel (shapeless, simplified)
  {
    pattern: ['G'],
    key: { 'G': B(BlockId.GRAVEL) },
    result: { itemId: ItemId.FLINT, count: 1 },
    shapeless: true,
    gridSize: 2,
  },

  // Mossy cobblestone (alternate): cobblestone + vine
  {
    pattern: ['CV'],
    key: { 'C': B(BlockId.COBBLESTONE), 'V': B(BlockId.VINE) },
    result: { blockId: BlockId.MOSS_STONE, count: 1 },
    shapeless: true,
    gridSize: 2,
  },

  // Painting: wool + sticks -> painting item
  {
    pattern: ['SSS', 'SWS', 'SSS'],
    key: { 'S': I(ItemId.STICK), 'W': B(BlockId.WOOL_WHITE) },
    result: { itemId: ItemId.PAINTING, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== ARMOR RECIPES (3x3) =====

  // Iron Helmet: II_ / I_I / ___
  {
    pattern: ['III', 'I I'],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.IRON_HELMET, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Iron Chestplate: I_I / III / III
  {
    pattern: ['I I', 'III', 'III'],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.IRON_CHESTPLATE, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Iron Leggings: III / I_I / I_I
  {
    pattern: ['III', 'I I', 'I I'],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.IRON_LEGGINGS, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Iron Boots: ___ / I_I / I_I
  {
    pattern: ['I I', 'I I'],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.IRON_BOOTS, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== GOLD ARMOR RECIPES (3x3) =====

  // Gold Helmet: GGG / G_G
  {
    pattern: ['GGG', 'G G'],
    key: { 'G': I(ItemId.GOLD_INGOT) },
    result: { itemId: ItemId.GOLD_HELMET, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Gold Chestplate: G_G / GGG / GGG
  {
    pattern: ['G G', 'GGG', 'GGG'],
    key: { 'G': I(ItemId.GOLD_INGOT) },
    result: { itemId: ItemId.GOLD_CHESTPLATE, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Gold Leggings: GGG / G_G / G_G
  {
    pattern: ['GGG', 'G G', 'G G'],
    key: { 'G': I(ItemId.GOLD_INGOT) },
    result: { itemId: ItemId.GOLD_LEGGINGS, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Gold Boots: G_G / G_G
  {
    pattern: ['G G', 'G G'],
    key: { 'G': I(ItemId.GOLD_INGOT) },
    result: { itemId: ItemId.GOLD_BOOTS, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== DIAMOND ARMOR RECIPES (3x3) =====

  // Diamond Helmet: DDD / D_D
  {
    pattern: ['DDD', 'D D'],
    key: { 'D': I(ItemId.DIAMOND_GEM) },
    result: { itemId: ItemId.DIAMOND_HELMET, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Diamond Chestplate: D_D / DDD / DDD
  {
    pattern: ['D D', 'DDD', 'DDD'],
    key: { 'D': I(ItemId.DIAMOND_GEM) },
    result: { itemId: ItemId.DIAMOND_CHESTPLATE, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Diamond Leggings: DDD / D_D / D_D
  {
    pattern: ['DDD', 'D D', 'D D'],
    key: { 'D': I(ItemId.DIAMOND_GEM) },
    result: { itemId: ItemId.DIAMOND_LEGGINGS, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Diamond Boots: D_D / D_D
  {
    pattern: ['D D', 'D D'],
    key: { 'D': I(ItemId.DIAMOND_GEM) },
    result: { itemId: ItemId.DIAMOND_BOOTS, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== UTILITY RECIPES (3x3) =====

  // Bucket: I_I / _I_ / ___
  {
    pattern: ['I I', ' I '],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.BUCKET, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Minecart: ___ / I_I / III
  {
    pattern: ['I I', 'III'],
    key: { 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.MINECART, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Rails: I_I / ISI / I_I where I=iron, S=stick -> 16 rails
  {
    pattern: ['I I', 'ISI', 'I I'],
    key: { 'I': I(ItemId.IRON_INGOT), 'S': I(ItemId.STICK) },
    result: { itemId: ItemId.RAILS, count: 16 },
    shapeless: false,
    gridSize: 3,
  },

  // Bookshelf (alternate recipe): PPP / BBB / PPP where B=book
  {
    pattern: ['PPP', 'BBB', 'PPP'],
    key: { 'P': B(BlockId.PLANKS), 'B': I(ItemId.BOOK) },
    result: { blockId: BlockId.BOOKSHELF, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Piston: PPP / CIC / CRC where P=planks, C=cobblestone, I=iron ingot, R=redstone
  {
    pattern: ['PPP', 'CIC', 'CRC'],
    key: { 'P': B(BlockId.PLANKS), 'C': B(BlockId.COBBLESTONE), 'I': I(ItemId.IRON_INGOT), 'R': I(ItemId.REDSTONE_DUST) },
    result: { itemId: ItemId.PISTON, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== 2x2 RECIPES =====

  // Bricks block: BB / BB where B=brick item -> bricks block
  {
    pattern: ['BB', 'BB'],
    key: { 'B': I(ItemId.BRICK_ITEM) },
    result: { blockId: BlockId.BRICKS, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Snow block: SS / SS where S=snowball -> snow block
  {
    pattern: ['SS', 'SS'],
    key: { 'S': I(ItemId.SNOWBALL) },
    result: { blockId: BlockId.SNOW, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Glowstone: GG / GG where G=glowstone_dust -> glowstone block
  {
    pattern: ['GG', 'GG'],
    key: { 'G': I(ItemId.GLOWSTONE_DUST) },
    result: { blockId: BlockId.GLOWSTONE, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // ===== REDSTONE RECIPES =====

  // Antorcha de Redstone: redstone dust + stick (vertical 2x1)
  {
    pattern: ['R', 'S'],
    key: { 'R': I(ItemId.REDSTONE_DUST), 'S': I(ItemId.STICK) },
    result: { blockId: BlockId.REDSTONE_TORCH, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Palanca (Lever): piedra + antorcha de redstone (simplified)
  {
    pattern: ['T', 'C'],
    key: { 'T': B(BlockId.REDSTONE_TORCH), 'C': B(BlockId.COBBLESTONE) },
    result: { blockId: BlockId.LEVER, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Boton (Button): 1 piedra en el centro
  {
    pattern: ['S'],
    key: { 'S': B(BlockId.STONE) },
    result: { blockId: BlockId.BUTTON, count: 1 },
    shapeless: true,
    gridSize: 2,
  },

  // Placa de presion (Pressure Plate): 2 piedras horizontal
  {
    pattern: ['SS'],
    key: { 'S': B(BlockId.STONE) },
    result: { blockId: BlockId.PRESSURE_PLATE, count: 1 },
    shapeless: false,
    gridSize: 2,
  },

  // Repetidor: 3 piedra + 2 redstone dust + 1 antorcha redstone
  {
    pattern: ['TRT', 'SSS'],
    key: { 'T': B(BlockId.REDSTONE_TORCH), 'R': I(ItemId.REDSTONE_DUST), 'S': B(BlockId.STONE) },
    result: { blockId: BlockId.REPEATER, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== INTERACTIVE BLOCK RECIPES =====

  // Trapdoor: 6 tablas (3x2) -> 2 trapdoors
  {
    pattern: ['PPP', 'PPP'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.TRAPDOOR, count: 2 },
    shapeless: false,
    gridSize: 3,
  },

  // Cartel (Sign): 6 tablas + 1 palo -> 3 carteles
  {
    pattern: ['PPP', 'PPP', ' S '],
    key: { 'P': B(BlockId.PLANKS), 'S': I(ItemId.STICK) },
    result: { blockId: BlockId.SIGN, count: 3 },
    shapeless: false,
    gridSize: 3,
  },

  // Jukebox: 8 tablas + 1 diamante -> 1 jukebox
  {
    pattern: ['PPP', 'PDP', 'PPP'],
    key: { 'P': B(BlockId.PLANKS), 'D': I(ItemId.DIAMOND_GEM) },
    result: { blockId: BlockId.JUKEBOX, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Noteblock: 8 tablas + 1 polvo de redstone -> 1 noteblock
  {
    pattern: ['PPP', 'PRP', 'PPP'],
    key: { 'P': B(BlockId.PLANKS), 'R': I(ItemId.REDSTONE_DUST) },
    result: { blockId: BlockId.NOTEBLOCK, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Escaleras de piedra: 6 piedra en forma de escalera -> 4 stairs
  {
    pattern: ['S  ', 'SS ', 'SSS'],
    key: { 'S': B(BlockId.STONE) },
    result: { blockId: BlockId.STAIRS_STONE, count: 4 },
    shapeless: false,
    gridSize: 3,
  },

  // Escaleras de madera: 6 tablas en forma de escalera -> 4 stairs
  {
    pattern: ['P  ', 'PP ', 'PPP'],
    key: { 'P': B(BlockId.PLANKS) },
    result: { blockId: BlockId.STAIRS_WOOD, count: 4 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== MAP & PAPER RECIPES =====

  // Papel: 3 semillas en fila -> 3 papel
  {
    pattern: ['SSS'],
    key: { 'S': I(ItemId.SEEDS) },
    result: { itemId: ItemId.PAPER, count: 3 },
    shapeless: false,
    gridSize: 3,
  },

  // Mapa: 8 papel + 1 polvo de redstone en el centro -> 1 mapa
  {
    pattern: ['PPP', 'PRP', 'PPP'],
    key: { 'P': I(ItemId.PAPER), 'R': I(ItemId.REDSTONE_DUST) },
    result: { itemId: ItemId.MAP, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== FISHING ROD RECIPE =====
  // Pattern (diagonal):
  //   __S
  //   _SH
  //   S_H
  // S=stick, H=string
  {
    pattern: ['  S', ' SH', 'S H'],
    key: { 'S': I(ItemId.STICK), 'H': I(ItemId.STRING) },
    result: { itemId: ItemId.FISHING_ROD, count: 1, durability: 65, maxDurability: 65 },
    shapeless: false,
    gridSize: 3,
  },

  // ===== END RECIPES =====
  // Blaze Powder: 1 Blaze Rod -> 2 Blaze Powder (shapeless, 1x1)
  {
    pattern: ['R'],
    key: { 'R': I(ItemId.BLAZE_ROD) },
    result: { itemId: ItemId.BLAZE_POWDER, count: 2 },
    shapeless: true,
    gridSize: 2,
  },
  // Ender Eye: Ender Pearl + Blaze Powder (shapeless 2x2)
  {
    pattern: ['PB'],
    key: { 'P': I(ItemId.ENDER_PEARL), 'B': I(ItemId.BLAZE_POWDER) },
    result: { itemId: ItemId.ENDER_EYE, count: 1 },
    shapeless: true,
    gridSize: 2,
  },
  // End Stone Bricks: 4 End Stone in 2x2
  {
    pattern: ['EE', 'EE'],
    key: { 'E': B(BlockId.END_STONE) },
    result: { blockId: BlockId.END_STONE_BRICKS, count: 4 },
    shapeless: false,
    gridSize: 2,
  },

  // ===== EXPLORATION & ADVENTURE RECIPES (Fase 7) =====

  // Shield: 1 iron ingot top center + 6 planks fill rest
  {
    pattern: ['PIP', 'PPP', ' P '],
    key: { 'P': B(BlockId.PLANKS), 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.SHIELD, count: 1 },
    shapeless: false,
    gridSize: 3,
  },

  // Crossbow (simplified 2x2): 2 sticks + 1 string + 1 iron ingot
  {
    pattern: ['SH', 'IS'],
    key: { 'S': I(ItemId.STICK), 'H': I(ItemId.STRING), 'I': I(ItemId.IRON_INGOT) },
    result: { itemId: ItemId.CROSSBOW, count: 1 },
    shapeless: false,
    gridSize: 2,
  },
];

// Furnace/smelting recipes: input -> output
const FURNACE_RECIPES = [
  // Blocks
  { input: B(BlockId.SAND), result: { blockId: BlockId.GLASS, count: 1 } },
  { input: B(BlockId.COBBLESTONE), result: { blockId: BlockId.STONE, count: 1 } },
  { input: B(BlockId.CLAY), result: { blockId: BlockId.BRICKS, count: 1 } },
  { input: B(BlockId.NETHERRACK), result: { blockId: BlockId.NETHER_BRICKS, count: 1 } },

  // Raw ores -> ingots
  { input: I(ItemId.RAW_IRON), result: { itemId: ItemId.IRON_INGOT, count: 1 } },
  { input: I(ItemId.RAW_GOLD), result: { itemId: ItemId.GOLD_INGOT, count: 1 } },

  // Ore blocks -> ingots (if player silk-touches or uses them directly)
  { input: B(BlockId.IRON_ORE), result: { itemId: ItemId.IRON_INGOT, count: 1 } },
  { input: B(BlockId.GOLD_ORE), result: { itemId: ItemId.GOLD_INGOT, count: 1 } },

  // Raw meats -> cooked meats
  { input: I(ItemId.RAW_BEEF), result: { itemId: ItemId.COOKED_BEEF, count: 1 } },
  { input: I(ItemId.RAW_PORK), result: { itemId: ItemId.COOKED_PORK, count: 1 } },
  { input: I(ItemId.RAW_CHICKEN), result: { itemId: ItemId.COOKED_CHICKEN, count: 1 } },

  // Raw fish -> cooked fish
  { input: I(ItemId.RAW_COD), result: { itemId: ItemId.COOKED_COD, count: 1 } },
  { input: I(ItemId.RAW_SALMON), result: { itemId: ItemId.COOKED_SALMON, count: 1 } },

  // Wood logs -> charcoal (coal)
  { input: B(BlockId.WOOD), result: { itemId: ItemId.COAL, count: 1 } },
  { input: B(BlockId.LOG_BIRCH), result: { itemId: ItemId.COAL, count: 1 } },
  { input: B(BlockId.JUNGLE_LOG), result: { itemId: ItemId.COAL, count: 1 } },
  { input: B(BlockId.ACACIA_LOG), result: { itemId: ItemId.COAL, count: 1 } },

  // Cactus -> green dye placeholder (just gives emerald-ish color)
  { input: B(BlockId.CACTUS), result: { itemId: ItemId.BONE_MEAL, count: 1 } },
];

// Fuel values: how many items each fuel can smelt
const FUEL_VALUES = {
  // Blocks (by blockId)
  blocks: {
    [BlockId.PLANKS]: 1.5,
    [BlockId.WOOD]: 1.5,
    [BlockId.BOOKSHELF]: 1.5,
    [BlockId.FENCE]: 1.5,
    [BlockId.SLAB_WOOD]: 0.75,
    [BlockId.LADDER]: 1.5,
  },
  // Items (by itemId)
  items: {
    [ItemId.STICK]: 0.5,
    [ItemId.COAL]: 8,
    [ItemId.CRAFTING_TABLE]: 1.5,
  },
};

/**
 * Get fuel value for an item (how many items it can smelt).
 * Returns 0 if not a valid fuel.
 */
export function getFuelValue(item) {
  if (!item) return 0;
  if (item.blockId !== undefined && FUEL_VALUES.blocks[item.blockId]) {
    return FUEL_VALUES.blocks[item.blockId];
  }
  if (item.itemId !== undefined && FUEL_VALUES.items[item.itemId]) {
    return FUEL_VALUES.items[item.itemId];
  }
  return 0;
}

const SMELT_DURATION = 10; // seconds to smelt one item

export class CraftingSystem {
  constructor() {
    this.recipes = SHAPED_RECIPES;
    this.furnaceRecipes = FURNACE_RECIPES;
  }

  /**
   * Check if a crafting grid matches any recipe.
   * @param {Array<Array<Object|null>>} grid - 2D array of items. Each cell is:
   *   null, { blockId } or { itemId } (with optional count)
   * @returns {Object|null} The result item, or null if no match.
   *   Result is: { blockId, count }, { itemId, count }, or a tool object from createTool()
   */
  checkRecipe(grid) {
    const gridSize = grid.length; // 2 or 3

    // Normalize the grid: trim empty rows/columns to find the actual pattern bounds
    const bounds = this._getPatternBounds(grid);
    if (!bounds) return null; // empty grid

    const { minR, maxR, minC, maxC } = bounds;
    const patternH = maxR - minR + 1;
    const patternW = maxC - minC + 1;

    // Try each recipe
    for (const recipe of this.recipes) {
      // Grid must be large enough for the recipe's minimum grid size
      if (gridSize < recipe.gridSize) continue;

      if (recipe.shapeless) {
        if (this._matchShapeless(grid, recipe, bounds)) {
          return this._createResult(recipe.result);
        }
      } else {
        // Try matching the pattern at the trimmed position
        if (this._matchShaped(grid, recipe, bounds)) {
          return this._createResult(recipe.result);
        }
        // Also try mirrored (for asymmetric recipes like axes)
        if (this._matchShapedMirrored(grid, recipe, bounds)) {
          return this._createResult(recipe.result);
        }
      }
    }

    return null;
  }

  /**
   * Get the bounding box of non-empty cells in the grid
   */
  _getPatternBounds(grid) {
    let minR = Infinity, maxR = -1, minC = Infinity, maxC = -1;
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[r].length; c++) {
        if (grid[r][c]) {
          minR = Math.min(minR, r);
          maxR = Math.max(maxR, r);
          minC = Math.min(minC, c);
          maxC = Math.max(maxC, c);
        }
      }
    }
    if (maxR === -1) return null;
    return { minR, maxR, minC, maxC };
  }

  /**
   * Match a shaped recipe at the trimmed position in the grid
   */
  _matchShaped(grid, recipe, bounds) {
    const { minR, maxR, minC, maxC } = bounds;
    const patternH = maxR - minR + 1;
    const patternW = maxC - minC + 1;

    // Get recipe pattern dimensions
    const recipeH = recipe.pattern.length;
    const recipeW = Math.max(...recipe.pattern.map(row => row.length));

    if (patternH !== recipeH || patternW !== recipeW) return false;

    for (let r = 0; r < recipeH; r++) {
      for (let c = 0; c < recipeW; c++) {
        const patChar = (recipe.pattern[r] || '')[c] || ' ';
        const gridCell = grid[minR + r][minC + c];

        if (patChar === ' ') {
          // Should be empty
          if (gridCell) return false;
        } else {
          // Should match the ingredient
          if (!gridCell) return false;
          const ingredient = recipe.key[patChar];
          if (!ingredient) return false;
          if (!this._matchIngredient(gridCell, ingredient)) return false;
        }
      }
    }

    return true;
  }

  /**
   * Match a shaped recipe mirrored horizontally
   */
  _matchShapedMirrored(grid, recipe, bounds) {
    const { minR, maxR, minC, maxC } = bounds;
    const patternH = maxR - minR + 1;
    const patternW = maxC - minC + 1;

    const recipeH = recipe.pattern.length;
    const recipeW = Math.max(...recipe.pattern.map(row => row.length));

    if (patternH !== recipeH || patternW !== recipeW) return false;

    for (let r = 0; r < recipeH; r++) {
      for (let c = 0; c < recipeW; c++) {
        const mirroredC = recipeW - 1 - c;
        const patChar = (recipe.pattern[r] || '')[mirroredC] || ' ';
        const gridCell = grid[minR + r][minC + c];

        if (patChar === ' ') {
          if (gridCell) return false;
        } else {
          if (!gridCell) return false;
          const ingredient = recipe.key[patChar];
          if (!ingredient) return false;
          if (!this._matchIngredient(gridCell, ingredient)) return false;
        }
      }
    }

    return true;
  }

  /**
   * Match a shapeless recipe: just check that all ingredients are present
   */
  _matchShapeless(grid, recipe, bounds) {
    // Collect all non-empty cells
    const gridItems = [];
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[r].length; c++) {
        if (grid[r][c]) gridItems.push(grid[r][c]);
      }
    }

    // Collect all required ingredients from pattern
    const required = [];
    for (const row of recipe.pattern) {
      for (const ch of row) {
        if (ch !== ' ') {
          required.push(recipe.key[ch]);
        }
      }
    }

    if (gridItems.length !== required.length) return false;

    // Try to match each required ingredient to a grid item
    const used = new Array(gridItems.length).fill(false);
    for (const req of required) {
      let found = false;
      for (let i = 0; i < gridItems.length; i++) {
        if (!used[i] && this._matchIngredient(gridItems[i], req)) {
          used[i] = true;
          found = true;
          break;
        }
      }
      if (!found) return false;
    }

    return true;
  }

  /**
   * Check if a grid cell matches an ingredient definition
   */
  _matchIngredient(cell, ingredient) {
    if (ingredient.blockId !== undefined) {
      return cell.blockId === ingredient.blockId;
    }
    if (ingredient.itemId !== undefined) {
      return cell.itemId === ingredient.itemId;
    }
    return false;
  }

  /**
   * Create the result item from a recipe result definition
   */
  _createResult(result) {
    if (result.toolType) {
      // Tool result
      return createTool(result.toolType, result.material);
    }
    if (result.blockId !== undefined) {
      return { blockId: result.blockId, count: result.count || 1 };
    }
    if (result.itemId !== undefined) {
      const item = { itemId: result.itemId, count: result.count || 1 };
      // Copy durability fields if present (e.g. fishing rod)
      if (result.durability !== undefined) {
        item.durability = result.durability;
        item.maxDurability = result.maxDurability || result.durability;
      }
      return item;
    }
    return null;
  }

  /**
   * Check a furnace/smelting recipe
   * @param {Object} inputItem - the item to smelt ({ blockId } or { itemId })
   * @returns {Object|null} result item or null
   */
  checkFurnaceRecipe(inputItem) {
    if (!inputItem) return null;
    for (const recipe of this.furnaceRecipes) {
      if (this._matchIngredient(inputItem, recipe.input)) {
        return { ...recipe.result };
      }
    }
    return null;
  }

  /**
   * Get all recipes for display in UI
   * @returns {Array} array of recipe objects
   */
  getAllRecipes() {
    return [...this.recipes];
  }

  /**
   * Get all furnace recipes for display
   * @returns {Array}
   */
  getAllFurnaceRecipes() {
    return [...this.furnaceRecipes];
  }

  /**
   * Get the number of each ingredient consumed when crafting.
   * Each filled slot in the pattern consumes 1 item.
   * Call this after checkRecipe succeeds to know how to decrement the grid.
   * @param {Array<Array<Object|null>>} grid
   * @returns {Array<Array<number>>} 2D array of amounts to consume (0 or 1)
   */
  getConsumption(grid) {
    const result = [];
    for (let r = 0; r < grid.length; r++) {
      result[r] = [];
      for (let c = 0; c < grid[r].length; c++) {
        result[r][c] = grid[r][c] ? 1 : 0;
      }
    }
    return result;
  }
}
